import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircleIcon } from "@heroicons/react/solid";

interface GenerationLoadingViewProps {
  phaseKeys: string[];
  stepIntervalMs?: number;
}

export const GenerationLoadingView = ({
  phaseKeys,
  stepIntervalMs = 9000,
}: GenerationLoadingViewProps) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentStep((prev) => Math.min(prev + 1, phaseKeys.length - 1));
    }, stepIntervalMs);
    return () => clearInterval(timer);
  }, [phaseKeys.length, stepIntervalMs]);

  return (
    <div className="flex flex-col items-center py-10 px-6 text-center">
      <div className="relative flex items-center justify-center mb-8">
        <span className="absolute h-16 w-16 rounded-full bg-cyan-100 animate-ping opacity-40" />
        <div className="relative h-12 w-12 rounded-full bg-cyan-50 border border-cyan-200 flex items-center justify-center">
          <svg
            className="h-6 w-6 text-cyan-600 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8H4z"
            />
          </svg>
        </div>
      </div>

      <ol className="w-full max-w-xs space-y-3 text-left mb-6">
        {phaseKeys.map((key, i) => {
          const done = i < currentStep;
          const current = i === currentStep;
          return (
            <li key={key} className="flex items-center gap-3">
              {done ? (
                <CheckCircleIcon
                  className="h-4 w-4 text-green-500 flex-shrink-0 animate-scale-in"
                  aria-hidden="true"
                />
              ) : current ? (
                <svg
                  className="h-4 w-4 text-cyan-600 animate-spin flex-shrink-0"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8H4z"
                  />
                </svg>
              ) : (
                <span
                  className="h-4 w-4 rounded-full border border-gray-200 flex-shrink-0"
                  aria-hidden="true"
                />
              )}
              <span
                className={`text-sm ${
                  done
                    ? "text-gray-400"
                    : current
                    ? "text-gray-900 font-medium"
                    : "text-gray-300"
                }`}
              >
                {t(key)}
              </span>
            </li>
          );
        })}
      </ol>

      <p className="text-xs text-gray-400">{t("risks.ai.loading.keepOpen")}</p>
    </div>
  );
};
